import React, { useState, useEffect, memo } from 'react';
import { TrendingUp, TrendingDown, Activity, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export const OptionsPanel = memo(({ spotPrice, isLive }) => {
  const [optionsData, setOptionsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState(null);

  // Fetch options data periodically
  useEffect(() => {
    if (!isLive) return;
    
    const fetchOptions = async () => {
      try {
        const res = await fetch(`${API}/api/options-data`);
        const data = await res.json();
        if (data.success && (data.data.call_oi > 0 || data.data.put_oi > 0)) {
          setOptionsData(data.data);
          setError(null);
          setSubscribed(true);
        }
      } catch (err) {
        console.error('Failed to fetch options:', err);
      }
    };

    fetchOptions();
    const interval = setInterval(fetchOptions, 2000);
    
    return () => clearInterval(interval);
  }, [isLive]);

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      const price = spotPrice || 25400;
      const res = await fetch(`${API}/api/subscribe-options?spot_price=${price}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setSubscribed(true);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const formatOI = (oi) => {
    if (oi >= 10000000) return `${(oi / 10000000).toFixed(2)}Cr`;
    if (oi >= 100000) return `${(oi / 100000).toFixed(2)}L`;
    if (oi >= 1000) return `${(oi / 1000).toFixed(1)}K`;
    return oi.toLocaleString();
  };

  if (!isLive) {
    return (
      <div className="p-2 bg-surface/50 border-t border-border">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground uppercase">Options OI</span>
          <span className="text-[10px] text-muted-foreground">Switch to LIVE</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 bg-surface/30" data-testid="options-panel">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-muted-foreground uppercase flex items-center gap-1">
          <Activity className="w-3 h-3 text-accent" />
          ATM Options
        </span>
        {!subscribed && !optionsData && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleSubscribe}
            disabled={loading}
            className="h-5 px-2 text-[10px]"
            data-testid="subscribe-options-btn"
          >
            {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Load'}
          </Button>
        )}
      </div>

      {error && (
        <p className="text-[10px] text-sell mb-1">{error}</p>
      )}

      {optionsData ? (
        <div className="space-y-2">
          {/* OI Summary Row */}
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className="bg-surface p-1.5 rounded">
              <div className="text-[8px] text-muted-foreground uppercase">Call OI</div>
              <div className="text-xs font-mono text-buy font-bold">
                {formatOI(optionsData.call_oi)}
              </div>
            </div>
            <div className="bg-surface p-1.5 rounded">
              <div className="text-[8px] text-muted-foreground uppercase">Put OI</div>
              <div className="text-xs font-mono text-sell font-bold">
                {formatOI(optionsData.put_oi)}
              </div>
            </div>
            <div className="bg-surface p-1.5 rounded">
              <div className="text-[8px] text-muted-foreground uppercase">PCR</div>
              <div className={`text-xs font-mono font-bold ${optionsData.pcr > 1 ? 'text-buy' : 'text-sell'}`}>
                {optionsData.pcr.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Compact Options Table */}
          {optionsData.call_options?.length > 0 && (
            <div className="overflow-hidden">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-muted-foreground border-b border-border/50">
                    <th className="text-left py-0.5 font-normal">CE</th>
                    <th className="text-center py-0.5 font-normal">Strike</th>
                    <th className="text-right py-0.5 font-normal">PE</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const strikes = new Set([
                      ...optionsData.call_options.map(o => o.strike),
                      ...optionsData.put_options.map(o => o.strike)
                    ]);
                    const sortedStrikes = Array.from(strikes).sort((a, b) => b - a);
                    
                    return sortedStrikes.map(strike => {
                      const ce = optionsData.call_options.find(o => o.strike === strike);
                      const pe = optionsData.put_options.find(o => o.strike === strike);
                      const isATM = Math.abs(strike - (spotPrice || 25400)) < 60;
                      
                      return (
                        <tr 
                          key={strike} 
                          className={`border-b border-border/30 ${isATM ? 'bg-accent/10' : ''}`}
                        >
                          <td className="py-1 text-buy font-mono">
                            {ce ? formatOI(ce.oi) : '-'}
                          </td>
                          <td className={`py-1 text-center font-mono font-bold ${isATM ? 'text-accent' : 'text-foreground'}`}>
                            {strike}
                          </td>
                          <td className="py-1 text-right text-sell font-mono">
                            {pe ? formatOI(pe.oi) : '-'}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {/* PCR Interpretation */}
          <div className="text-[9px] text-muted-foreground flex items-center justify-center gap-2 pt-1 border-t border-border/30">
            <span className={optionsData.pcr > 1 ? 'text-buy' : 'text-sell'}>
              {optionsData.pcr > 1 ? '↑ Bullish' : '↓ Bearish'} Sentiment
            </span>
          </div>
        </div>
      ) : (
        <div className="text-[10px] text-muted-foreground text-center py-2">
          {subscribed ? 'Loading...' : 'Click Load to fetch options'}
        </div>
      )}
    </div>
  );
});

OptionsPanel.displayName = 'OptionsPanel';

export default OptionsPanel;
